import { rem } from 'polished'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import styled from 'styled-components'

import { bedbankOfferProductDetailView } from 'actions/AnalyticsActions'
import { fetchBedbankOfferById } from 'actions/BedbankOfferActions'
import { fetchTraderInformation, offerViewed } from 'actions/OfferActions'
import { accommodationOfferClicked } from 'analytics/eventDefinitions'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import HeadData from 'components/Common/HeadData'
import HttpStatus from 'components/Common/HttpStatus'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import Pane from 'components/Common/Pane'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import BedbankOfferContainer from 'components/OfferPage/Bedbank/BedbankOfferContainer'
import BedbankNavigableOfferContent from 'components/OfferPage/NavigableOfferContent/BedbankNavigableOfferContent'
import PropertyContainer from 'components/OfferPage/Property/PropertyContainer'
import { BottomMobileRelatedTravelItems } from 'components/OfferPage/RelatedTravelItems'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import OfferYmalStickyFooter from 'components/Recommendations/YouMayAlsoLike/Offer/OfferYmalStickyFooter'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { urlHashDatePicker } from 'constants/url'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import type { OfferPageDispatchAction, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import useProperty from 'hooks/Offers/useProperty'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED } from 'lib/offer/highIntentOffers'
import offerPageURL from 'lib/offer/offerPageURL'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { ReducerMiddleware } from 'lib/redux/reducerUtils'
import { getDefaultAirport } from 'selectors/flightsSelectors'
import { getInitialBedbankOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import bedbankMobileFilterMiddleware from 'components/OfferPage/Bedbank/MobileBookingFlow/BedbankMobileFilterMiddleware'
import { getNextFilterWithCapacityCheckInMiddleware } from 'components/OfferPage/utils'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [bedbankMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddleware]

const MainContent = styled(Group)`
  flex-grow: 1;
`

const NavigableContentPane = styled(Pane)`
  padding-bottom: ${rem(24)};
`
const OfferPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ObservableWrapper = styled.div`
  padding-top: 1px;
`

interface Props extends RouteComponentProps<{ offerSlug: string, offerId: string }> {
  fetching: boolean
  offer?: App.BedbankOffer
  offerError?: any
  regionCode: string
  currency: string
  headlessMode: boolean
  traderInformation?: App.TraderInformation
  relatedTravelItems: App.OfferState['relatedTravelItems']
  lereLoadingError?: any
  propertyPageOfferId?: string
  isSpoofed: boolean
}

function BedbankOfferPage(props: Props) {
  const {
    fetching,
    match,
    offer,
    offerError,
    regionCode,
    currency,
    location,
    headlessMode,
    relatedTravelItems,
    traderInformation,
    propertyPageOfferId,
    isSpoofed,
  } = props

  const dispatch = useAppDispatch()
  const [showNavOfferContent, setShowNavOfferContent] = useState(false)

  const offerId = propertyPageOfferId || match.params.offerId
  const dataLoaded = useRef(false)

  // Keep this last condition consistent with `fetchBedbankOfferAndBreadcrumbs.ts`.
  const shouldUsePropertyPage = config.ACCOMM_USE_LPP_PROPERTY_PAGE && !offer?.promotions.length
  const [propertyMapping, fetchingPropertyMapping] = usePropertyOfferMapping(OFFER_TYPE_BED_BANK, offer?.id, {
    disabled: !shouldUsePropertyPage,
  })
  // TODO(HP-1614): This is a workaround for the accommodation service mapping endpoint eagerly
  // returning a mapping, even though bedbank rates are disabled, meaning we might not be guaranteed
  // to successfully fetch property content.
  const hasValidPropertyMapping = !!propertyMapping?.bedbankPropertyIds?.includes(offer?.id ?? '')
  const [property] = useProperty(propertyMapping?.propertyId, { disabled: !shouldUsePropertyPage })

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()
  const isMobileScreen = useScreenSizeOnly('mobile')

  const ymalStickyFooterShowTrigger = useInView({ triggerOnce: true })
  const ymalStickyFooterHideTrigger = useInView({ triggerOnce: false })
  const canShowYmalFooter = ymalStickyFooterShowTrigger.inView && !ymalStickyFooterHideTrigger.inView && !isMobileScreen

  const [offerPageDefaults, defaultAirport] = useAppSelector(
    state => [(offer ? getInitialBedbankOfferPageState(state, offer) : {}), getDefaultAirport(state)],
    shallowEqual,
  )
  const [middlewares, setMiddlewares] = useState<Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> | undefined>(undefined)
  const [offerPageState, offerPageDispatch] = useOfferPageReducer({
    ...offerPageDefaults,
    airport: defaultAirport,
    bedbankOffer: offer,
  }, middlewares)

  useEffect(() => {
    setMiddlewares(isMobileScreen ? mobileVariantMiddlewares : defaultMiddlewares)
  }, [isMobileScreen])

  /**
   * 1. go to a YMAL recommendation
   * 2. Refresh page (clears redux)
   * 3. Go back
   * 4. FETCH_BEDBANK_OFFER action fires, but reducer already computed its initialState where bedbankoffer was initially undefined
   * 5. Fetch done -> loading indicator disappears, component crashes
   *
   * This useEffect is a workaround that.
   */
  useEffect(() => {
    if (offer) {
      offerPageDispatch({ type: OfferPageActions.setBedBankOffer, bedbankOffer: offer })
      if ('duration' in offerPageDefaults && typeof offerPageDefaults.duration === 'number') {
        offerPageDispatch({ type: OfferPageActions.setDuration, duration: offerPageDefaults.duration })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer])
  useEffect(() => {
    if (offer) {
      // we use dataLoaded ref to prevent multiple analytics pushes
      fireEcommerceGTMEvent(accommodationOfferClicked(offer, currency))
      dataLoaded.current = true
    }
  }, [offer, currency])

  usePageActivityTracking()

  useEffect(() => {
    if (!showNavOfferContent && location.hash && location.hash !== urlHashDatePicker) {
      setShowNavOfferContent(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash])

  useEffect(() => {
    if (showNavOfferContent) {
      window.location.href = location.hash
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNavOfferContent])

  useEffect(() => {
    if (offer && ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offer.type)) {
      dispatch(offerViewed(offer.id, offer.type, !offer.sell?.price))
    }
  }, [offer, dispatch])

  // This one is used to load the section before the user gets to it
  const [navigableRef, navigableInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  useEffect(() => {
    dispatch(fetchBedbankOfferById(offerId, { regionCode }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  useEffect(() => {
    if (isEnabledForFeature(config.TRADER_INFORMATION_REGIONS!, regionCode)) {
      dispatch(fetchTraderInformation(offerId, regionCode))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  useEffect(() => {
    if (offer) {
      dispatch(bedbankOfferProductDetailView(offer))
      window.dataLayer?.push({ offerType: offer.type })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  const hasPromotions = (offer?.promotions ?? []).length > 0
  const showRecommendations = !!offer && !isSpoofed && !isMobileScreen

  const offerListFilters: App.OfferListFilters = useMemo(() => ({
    propertyId: `bedbank:${offerId}`,
    searchNearby: true,
    checkIn: offerPageState.checkInDate,
    checkOut: offerPageState.checkOutDate,
    rooms: offerPageState.rooms,
    lere: 'graph',
    baseOfferId: offerId,
  }), [offerId, offerPageState.checkInDate, offerPageState.checkOutDate, offerPageState.rooms])

  if (offerError) {
    if (offerError.status === 404) {
      return <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>
    } else {
      return <HttpStatus code={500}>
        <Error500Page error={offerError} />
      </HttpStatus>
    }
  }

  if (fetchingPropertyMapping || (hasValidPropertyMapping && !property) || fetching || !offer || !offerPageState.bedbankOffer) {
    return <LoadingIndicator data-testid="bedbank-offer-loading-indicator" />
  }

  if (match.params.offerSlug && (offer.slug !== match.params.offerSlug)) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  const metaDescription = `Book the best deal for ${offer.name}, ${offer.property.address.city} with Luxury Escapes today. The world's best escapes, handpicked by experts!`
  const breadCrumbItems = relatedTravelItems[offer?.id] || []

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    <HeadData
      title={`${offer.name}, ${offer.property.address.city} - ${config.title} ${regionCode}`}
      description={metaDescription}
      imageId={offer.images[0]?.id || undefined}
    />
    <OfferPageWrapper className="OfferPage">
      <MainContent direction="vertical" verticalAlign="space-between">
        <LuxPlusLabelProvider page="offer-page">
          {!property && (
            <OfferPageStateProvider state={offerPageState} dispatch={offerPageDispatch}>
              <BedbankOfferContainer
                offerId={offerId}
                offer={offer}
                ymalStickyFooterHideTriggerRef={ymalStickyFooterHideTrigger.ref}
                ymalStickyFooterShowTriggerRef={ymalStickyFooterShowTrigger.ref}
                canShowYmalFooter={canShowYmalFooter}
              />
              {!hasPromotions && showRecommendations && (
                <ErrorBoundary fallback={null}>
                  <OfferYmalStickyFooter
                    seedOffer={offer}
                    filters={offerListFilters}
                    show={canShowYmalFooter}
                    initialShowDelayMs={2000}
                    hideImmediately={!!offerPageState.activeFilter}
                    pageSource="Bedbank"
                  />
                </ErrorBoundary>
              )}
            </OfferPageStateProvider>
          )}
          {property && <PropertyContainer
            property={property}
            offer={offer}
            lereYmalStickyFooterShowTrigger={ymalStickyFooterShowTrigger}
            shouldUseLereStickyFooter
          />}
        </LuxPlusLabelProvider>
        {!property && <SoftOfferPageWalledGarden
          bypassWall={isWalledGardenEuropeOffersEnabled}
          wall={<OfferPageSignUpWall offerType="hotel" />}
        />}
        <ObservableWrapper ref={navigableRef}>
          <NavigableContentPane>
            {(navigableInView || showNavOfferContent) && (
              <BedbankNavigableOfferContent
                offer={offer}
                traderInformation={traderInformation}
                hash={location.hash}
              />
            )}
          </NavigableContentPane>
        </ObservableWrapper>
        <ObservableWrapper>
          {!headlessMode && <BottomMobileRelatedTravelItems offer={offer} items={breadCrumbItems} title="Travel Destinations" />}
        </ObservableWrapper>
        <ObservableWrapper>
          {hasPromotions && showRecommendations && (
            <ErrorBoundary fallback={null}>
              <OfferRecommendations
                title={`Similar to ${offer.property?.name ?? 'this'}`}
                filters={offerListFilters}
                offerId={offerId}
                trackingDescription="Offer LE"
                enableDateCheckingForPriceDisplay
              />
            </ErrorBoundary>
          )}
        </ObservableWrapper>
      </MainContent>
    </OfferPageWrapper>
  </AnalyticsPageContext.Provider>
}

const mapStateToProps = (state: App.State, ownProps: Partial<Props>) => {
  const offerId = ownProps.propertyPageOfferId || ownProps.match?.params?.offerId || ''
  const offer: App.BedbankOffer | undefined = state.offer.bedbankOffers[offerId]

  return {
    regionCode: state.geo.currentRegionCode,
    currency: state.geo.currentCurrency,
    fetching: !!state.offer.offersLoading[offerId] && !offer,
    offer,
    traderInformation: state.offer.traderInformation[offerId]?.data,
    offerError: state.offer.offerErrors[offerId],
    headlessMode: state.system.headlessMode,
    relatedTravelItems: state.offer.relatedTravelItems,
    isSpoofed: state.auth.account.isSpoofed,
  }
}

export default withRouter(connect(mapStateToProps)(BedbankOfferPage))
